import { createContext, useContext, useEffect, useState } from "react";
import * as React from "react";
import axios from "axios";
import {
  LibraryThingEnrichment,
  SyndeticsUnboundUtil,
} from "@tlc/syndetics-util";

interface SyndeticsConfig {
  active: boolean;
  accountNumber: string;
  clientCode: string;
  instanceId: string;
  branchFilters: string[];
}

interface SyndeticsConfigContextType {
  syndeticsConfig: SyndeticsConfig;
  enrichmentsEnabled: LibraryThingEnrichment[];
  hasRelatedTabEnrichments: boolean;
  hasReviewsTabEnrichments: boolean;
  hasMoreInfoTabEnrichments: boolean;
}

const SyndeticsConfigContext = createContext<SyndeticsConfigContextType>(null);

export const useSyndeticsConfig = () => {
  const context = useContext(SyndeticsConfigContext);

  if (!context) {
    throw new Error(
      "useSyndeticsConfig must be used within a SyndeticsConfigProvider"
    );
  }

  return context;
};

interface SyndeticsConfigHolderResponse {
  syndeticsConfig: SyndeticsConfig;
  enrichmentsEnabled: LibraryThingEnrichment[];
  syndeticsUnboundUtil: SyndeticsUnboundUtil;
}

// TODO update LibraryThingWidget to use SyndeticsConfigHolder or replace it
export const SyndeticsConfigHolder = (() => {
  const syndeticsUnboundUtil = new SyndeticsUnboundUtil();
  // TODO check location config instance handling

  let configRequested = false;
  let syndeticsConfig: SyndeticsConfig = null;
  let enrichmentsEnabled: LibraryThingEnrichment[] = null;
  const subscribers: ((event: SyndeticsConfigHolderResponse) => void)[] = []; // Array to hold subscriber functions

  const fetchConfig = () => {
    axios
      .get<SyndeticsConfig>("/syndetics-config")
      .then(({ data }) => {
        syndeticsConfig = { ...data };
        if (data.active === false) {
          enrichmentsEnabled = [];
          subscribers.forEach((subscriber) =>
            subscriber({
              syndeticsConfig,
              enrichmentsEnabled,
              syndeticsUnboundUtil,
            })
          );
          return;
        }
        const { accountNumber, instanceId } = data;
        try {
          syndeticsUnboundUtil
            .initScript(accountNumber, instanceId)
            .then((response) => {
              enrichmentsEnabled = response;
              subscribers.forEach((subscriber) =>
                subscriber({
                  syndeticsConfig,
                  enrichmentsEnabled,
                  syndeticsUnboundUtil,
                })
              );
            })
            .catch((e) => {
              console.error("Error getting enabled enrichments");
              enrichmentsEnabled = [];
              subscribers.forEach((subscriber) =>
                subscriber({
                  syndeticsConfig,
                  enrichmentsEnabled,
                  syndeticsUnboundUtil,
                })
              );
            });
        } catch (e) {
          console.error("Error calling syndeticsUnboundUtil.initScript", e);
        }
      })
      .catch((error) => {
        console.error("Error getting syndetics config", error);
      });
  };

  const subscribe = (
    subscriber: (event: SyndeticsConfigHolderResponse) => void
  ) => {
    subscribers.push(subscriber);
    if (!configRequested) {
      configRequested = true;
      fetchConfig();
    }

    if (syndeticsConfig && enrichmentsEnabled) {
      subscriber({
        syndeticsConfig: { ...syndeticsConfig },
        enrichmentsEnabled: [...enrichmentsEnabled],
        syndeticsUnboundUtil,
      });
    }

    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }
    };
  };

  return {
    subscribe,
  };
})();

export const SyndeticsConfigProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [syndeticsConfig, setSyndeticsConfig] = useState<SyndeticsConfig>(null);
  const [enrichmentsEnabled, setEnrichmentsEnabled] =
    useState<LibraryThingEnrichment[]>(null);
  const [hasRelatedTabEnrichments, setHasRelatedTabEnrichments] =
    useState<boolean>(null);
  const [hasMoreInfoTabEnrichments, setHasMoreInfoTabEnrichments] =
    useState<boolean>(null);
  const [hasReviewsTabEnrichments, setHasReviewsTabEnrichments] =
    useState<boolean>(null);
  useEffect(() => {
    const unsubscribe = SyndeticsConfigHolder.subscribe(
      ({ syndeticsConfig, enrichmentsEnabled }) => {
        setSyndeticsConfig(syndeticsConfig);
        setEnrichmentsEnabled(enrichmentsEnabled);

        setHasRelatedTabEnrichments(
          enrichmentsEnabled.some((enrichment) =>
            [
              LibraryThingEnrichment.AlternateSeries,
              LibraryThingEnrichment.AltmetricBadges,
              LibraryThingEnrichment.RCLCoreTitles,
              LibraryThingEnrichment.Tags,
              LibraryThingEnrichment.YouMayAlsoLike,
              LibraryThingEnrichment.Lists,
              LibraryThingEnrichment.AlsoAvailableAs,
              LibraryThingEnrichment.Series,
            ].includes(enrichment)
          )
        );

        setHasMoreInfoTabEnrichments(
          enrichmentsEnabled.some((enrichment) =>
            [
              LibraryThingEnrichment.Summary,
              LibraryThingEnrichment.VideoGameSummary,
              LibraryThingEnrichment.VideoAndMusicSummary,
              LibraryThingEnrichment.Awards,
              LibraryThingEnrichment.LookInside,
              LibraryThingEnrichment.BookProfile,
              LibraryThingEnrichment.AboutTheAuthor,
            ].includes(enrichment)
          )
        );

        setHasReviewsTabEnrichments(
          enrichmentsEnabled.some((enrichment) =>
            [LibraryThingEnrichment.ProfessionalReviews].includes(enrichment)
          )
        );
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const contextValue = {
    syndeticsConfig,
    enrichmentsEnabled,
    hasMoreInfoTabEnrichments,
    hasRelatedTabEnrichments,
    hasReviewsTabEnrichments,
  };

  return (
    <SyndeticsConfigContext.Provider value={contextValue}>
      {children}
    </SyndeticsConfigContext.Provider>
  );
};
